import type { HeaderAnalyticPayload } from '@jouzen/ecom-components/layout';
import type { EventProperties } from '@segment/analytics-next';
import { usePathname } from 'next/navigation';
import type { MouseEvent } from 'react';

import { EventType } from '@/analytics/types';

interface UsePageAnalytics {
  onFooterLinkClick: (
    event: MouseEvent<HTMLAnchorElement>,
    analyticCta: string,
  ) => void;
  onHamburgerClick: ({ open }: { open: boolean }) => void;
  onMenuLinkClick: (_: unknown, payload: HeaderAnalyticPayload) => void;
  sendEmailSubmitAnalytics: (email: string) => Promise<void>;
}

const usePageAnalytics = (): UsePageAnalytics => {
  const path = usePathname();

  // analytics on open and close the menu
  const handleHamburgerClick = ({ open }: { open: boolean }): void => {
    void window.ouraAnalytics.track(EventType.HamburgerClicked, {
      location: 'header',
      action: open ? 'open' : 'close',
    });
  };

  // analytics on click menu link
  const handleMenuLinkClick = (_: unknown, payload: HeaderAnalyticPayload) => {
    const { event, ...rest } = payload;
    void window.ouraAnalytics.track(
      event as EventType,
      rest as EventProperties,
    );
  };

  // analytics on click footer link
  const handleFooterLinkClick = (
    event: MouseEvent<HTMLAnchorElement>,
    analyticCta: string,
  ) => {
    const href = event.currentTarget.href;
    const url = new URL(href);
    const currentOrigin = window.location.origin;
    const _path = url.origin === currentOrigin ? url.pathname : href;

    void window.ouraAnalytics.track(EventType.LinkClicked, {
      cta: analyticCta,
      location: 'footer',
      path: _path,
    });
  };

  // submit email form in footer
  const sendEmailSubmitAnalytics = async (_email: string): Promise<void> => {
    const email = _email.toLowerCase();
    await window.ouraAnalytics.identify({ email });

    await Promise.all(
      [
        {
          name: EventType.EmailSignupCompleted,
          properties: { path, email },
        },
        {
          name: EventType.UserEmailCaptured,
          properties: { email },
        },
        { name: EventType.MarketingOptInFooter, properties: { email } },
      ].map(({ name, properties }) =>
        window.ouraAnalytics.track(name, properties),
      ),
    );
  };

  return {
    onHamburgerClick: handleHamburgerClick,
    onFooterLinkClick: handleFooterLinkClick,
    onMenuLinkClick: handleMenuLinkClick,
    sendEmailSubmitAnalytics,
  };
};

export default usePageAnalytics;
